import type { UpdateMeCoache } from '@entities/Authentication/types.ts';
import { useSaveData } from '@entities/common/savedData/index.ts';
import axios, { type AxiosProgressEvent } from 'axios';
import { useState } from 'react';
import { xhr } from '@shared/config';
import { coachVideoApi } from './api';

interface uploadResponse {
  id: number;
  progress_bar_url: string;
  upload_url: string;
}

interface VideoData {
  file: File | null;
  url: string;
  video_type: string;
}

export interface CoachVideo {
  id: number;
  screenshot_url: string;
  created: string;
  modified: string;
  code: string;
  player_code: string;
  metadata: {
    duration: string;
  };
  title: string;
  status_code: string;
  status_info: 'P' | 'D' | 'F' | 'I';
  folder: number;
  author: number;
}

export const useCoachVideo = () => {
  const [progress, setProgress] = useState<AxiosProgressEvent>();
  const [isLoading, setLoading] = useState(false);

  const instance = axios.create({
    withCredentials: false,
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
      setProgress(progressEvent);
    },
  });

  const uploadVideo = async (data: VideoData) => {
    setLoading(true);

    try {
      const uploadData = new FormData();
      uploadData.set('video_type', data.video_type);

      if (data.video_type === 'B' && data.file) {
        const uploadResponse = await xhr.post<uploadResponse>(coachVideoApi, null, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadData.set('file', data.file);
        await instance.post(uploadResponse.data.upload_url, uploadData);
        const checkRes = await xhr.get<CoachVideo>(
          `${coachVideoApi}${uploadResponse.data.id}/check/`
        );

        if (!checkRes.data) {
          throw new Error('Check error');
        }
        return uploadResponse.data.id;
      }

      if (data.video_type === 'Y') {
        uploadData.set('youtube_url', data.url);
        const uploadResponse = await xhr.post<uploadResponse>(coachVideoApi, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (!uploadResponse.data) {
          throw new Error('Check error');
        }
        return uploadResponse.data.id;
      }
    } catch (error) {
      throw new Error(`Upload error: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    uploadVideo,
    progress,
    isLoading,
  };
};

export const useCoacheEditorSavedData = () => {
  const {
    data: savedData,
    doSetStorageData,
    doRemoveStorageData,
  } = useSaveData<UpdateMeCoache>('coacheEditorSavedData');
  return {
    savedData,
    doSetStorageData,
    doRemoveStorageData,
  };
};
